import React, { useState, useEffect, useMemo } from 'react';
import MathHtml from '../../components/shared/MathHtml';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getDailyPaper, getDailyPaperHistory, tagDailyPaperError } from '../../api';

// Daily Paper — result.
// The submit response carries everything (summary, breakdown, full review) and is handed
// over in router state. A deep link or a refresh has no state, so we rebuild what we can:
// GET /daily-paper/{id} returns the answers, correct options, explanations and saved tags
// once a paper is completed, and the history row supplies the score. Only the
// "how it was built" and per-concept breakdowns are missing, and we say so.

function toHtml(s) { const t = s || ''; return t.indexOf('<') >= 0 ? t : t.split('\n').join('<br>'); }
function HtmlDiv(p) { return React.createElement(MathHtml, { tag: 'div', style: p.style, html: toHtml(p.html) }); }
function HtmlSpan(p) { return React.createElement(MathHtml, { tag: 'span', style: p.style, html: toHtml(p.html) }); }

const OPTS = ['A', 'B', 'C', 'D'];

// Plain English for the four ways a question got into the paper. No jargon on screen.
const BUCKETS = [
  { key: 'new', label: "Today's concepts", hint: 'Straight from what you were taught most recently.' },
  { key: 'error', label: 'Questions you got wrong before', hint: 'Coming back so they stop being weak spots.' },
  { key: 'revision', label: 'Older concepts, new questions', hint: 'Same topics, questions you have not seen.' },
  { key: 'retention', label: 'Things you knew two weeks ago', hint: 'Checking that what you learned has stuck.' },
];

const TAGS = [
  { key: 'concept', label: "Didn't know the concept" },
  { key: 'method', label: 'Knew it, wrong method' },
  { key: 'calculation', label: 'Calculation slip' },
  { key: 'misread', label: 'Misread the question' },
];

function statusFor(q) {
  if (q.is_correct) return { color: '#10B981', bg: '#D1FAE5', label: 'Correct' };
  if (q.is_attempted) return { color: '#EF4444', bg: '#FEE2E2', label: 'Wrong' };
  return { color: '#F59E0B', bg: '#FEF3C7', label: 'Skipped' };
}

// One review card. Wrong and skipped questions get the error tagger; correct ones do not.
function ReviewCard({ q, index, testId, tag, onTagged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const status = statusFor(q);
  const needsTag = !q.is_correct;

  const clickTag = async (key) => {
    if (busy) return;
    const next = tag === key ? null : key;   // clicking the selected one clears it
    setBusy(true); setErr('');
    try {
      await tagDailyPaperError(testId, { question_id: q.question_id, error_tag: next });
      onTagged(q.question_id, next);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not save that. Tap the button again.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ background: 'white', border: `2px solid ${status.color}`, borderRadius: 12, padding: 18, marginBottom: 14, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ background: status.bg, color: status.color, padding: '4px 10px', borderRadius: 8, fontWeight: 700, fontSize: 12 }}>
            Q{index + 1} · {status.label}
          </span>
          {q.module_name && (
            <span style={{ background: '#EEF2FF', color: '#4F46E5', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600 }}>
              {q.module_code ? `${q.module_code} · ` : ''}{q.module_name}
            </span>
          )}
        </div>
      </div>

      {q.passage_text && (
        <div style={{ background: '#F8FAFC', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, fontSize: 14, lineHeight: 1.9 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Passage</div>
          <HtmlDiv html={q.passage_text} />
        </div>
      )}

      <HtmlDiv html={q.question_text} style={{ fontSize: 15, lineHeight: 1.7, marginBottom: 14, fontWeight: 500 }} />
      {q.question_image_url && <img src={q.question_image_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 14, border: '1px solid var(--border)' }} />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {OPTS.map(opt => {
          const val = q[`option_${opt.toLowerCase()}`];
          const img = q[`option_${opt.toLowerCase()}_image_url`];
          if (val == null && !img) return null;
          const wasStudent = q.your_answer === opt;
          const isCorrect = q.correct_option === opt;
          let border = '#E2E8F0', bg = 'white';
          if (isCorrect) { border = '#10B981'; bg = '#D1FAE5'; }
          else if (wasStudent) { border = '#EF4444'; bg = '#FEE2E2'; }
          return (
            <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: `2px solid ${border}`, background: bg }}>
              <span style={{ width: 26, height: 26, borderRadius: 6, background: border, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{opt}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <HtmlSpan html={val} style={{ fontSize: 14, color: '#0F172A' }} />
                {img && <img src={img} alt="" style={{ maxWidth: '100%', borderRadius: 6, marginTop: 6 }} />}
              </div>
              {wasStudent && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#FFF', color: '#475569', flexShrink: 0 }}>Your answer</span>}
              {isCorrect && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#10B981', color: 'white', flexShrink: 0 }}>Correct</span>}
            </div>
          );
        })}
      </div>

      {!q.is_attempted && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>You left this one unanswered.</div>
      )}

      {(q.explanation || q.explanation_image_url) && (
        <div style={{ marginTop: 4, borderTop: '1px solid #E2E8F0', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Explanation</div>
          {q.explanation && <HtmlDiv html={q.explanation} style={{ fontSize: 14, lineHeight: 1.7, color: '#0F172A' }} />}
          {q.explanation_image_url && <img src={q.explanation_image_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginTop: 10, border: '1px solid var(--border)' }} />}
        </div>
      )}

      {needsTag && (
        <div style={{ marginTop: 14, borderTop: '1px solid #E2E8F0', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
            Why did this one go wrong?
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {TAGS.map(t => {
              const on = tag === t.key;
              return (
                <button key={t.key} disabled={busy} onClick={() => clickTag(t.key)}
                  title={on ? 'Tap again to remove this tag' : ''}
                  style={{
                    padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                    border: `2px solid ${on ? '#4F46E5' : '#CBD5E1'}`,
                    background: on ? '#4F46E5' : 'white',
                    color: on ? 'white' : '#475569',
                    opacity: busy ? 0.6 : 1,
                  }}>
                  {on ? '✓ ' : ''}{t.label}
                </button>
              );
            })}
          </div>
          {err && <div className="error-msg">{err}</div>}
        </div>
      )}
    </div>
  );
}

export default function DailyPaperResult() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // The happy path: submit handed us the whole result object in router state.
  const passed = location.state?.result || null;

  const [fallback, setFallback] = useState(null);      // { paper, historyRow } when there is no state
  const [loading, setLoading] = useState(!passed);
  const [loadError, setLoadError] = useState('');
  const [tags, setTags] = useState({});                // question_id -> error_tag

  // GET /daily-paper/{id} on a finished paper gives the same per-question fields under
  // slightly different names — reshape it so one set of review cards serves both paths.
  const rebuiltReview = useMemo(() => {
    const paper = fallback?.paper;
    if (!paper || paper.status === 'in_progress') return [];
    return (paper.questions || []).map(q => ({
      ...q,
      your_answer: q.student_answer || null,
      is_attempted: !!q.student_answer,
      is_correct: !!q.is_correct,
    }));
  }, [fallback]);

  // Seed the tagger from whatever the server already knows.
  useEffect(() => {
    const source = passed ? (passed.review || []) : rebuiltReview;
    if (!source.length) return;
    const seed = {};
    source.forEach(q => { if (q.error_tag) seed[q.question_id] = q.error_tag; });
    setTags(seed);
  }, [passed, rebuiltReview]);

  // Deep link / refresh: there is no "re-fetch the result" endpoint, so rebuild what we can.
  useEffect(() => {
    if (passed) return;
    let cancelled = false;
    setLoading(true); setLoadError('');
    Promise.all([
      getDailyPaper(testId).then(r => r.data).catch(() => null),
      getDailyPaperHistory(50).then(r => r.data).catch(() => null),
    ]).then(([paper, hist]) => {
      if (cancelled) return;
      if (!paper) {
        setLoadError('Could not load this paper. Check your connection and refresh the page.');
        setLoading(false);
        return;
      }
      const rows = Array.isArray(hist) ? hist : (hist?.papers || hist?.history || []);
      const row = rows.find(h => String(h.test_id) === String(testId)) || null;
      setFallback({ paper, historyRow: row });
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [passed, testId]);

  const onTagged = (qid, value) => {
    setTags(prev => {
      const next = { ...prev };
      if (value) next[qid] = value; else delete next[qid];
      return next;
    });
  };

  const review = passed ? (passed.review || []) : rebuiltReview;
  const needTagging = useMemo(() => review.filter(q => !q.is_correct), [review]);
  const taggedCount = needTagging.filter(q => tags[q.question_id]).length;

  const modules = useMemo(() => {
    const rows = [...(passed?.by_module || [])];
    // Worst first: lowest share correct, then most wrong.
    rows.sort((a, b) => {
      const ra = a.total ? a.correct / a.total : 0;
      const rb = b.total ? b.correct / b.total : 0;
      if (ra !== rb) return ra - rb;
      return (b.wrong || 0) - (a.wrong || 0);
    });
    return rows;
  }, [passed]);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}><div className="spinner" /></div>;

  if (loadError) {
    return (
      <div className="card" style={{ borderColor: 'var(--danger)', maxWidth: 620, margin: '40px auto' }}>
        <div style={{ color: 'var(--danger)', fontWeight: 600, marginBottom: 10 }}>{loadError}</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>Try again</button>
          <button className="btn btn-ghost" onClick={() => navigate('/student/daily-paper')}>Back to Daily Test</button>
        </div>
      </div>
    );
  }

  // ---------- FALLBACK SUMMARY (deep link or refresh) ----------
  if (!passed) {
    const paper = fallback?.paper;
    const row = fallback?.historyRow;
    const answered = (paper?.questions || []).filter(q => q.student_answer).length;
    return (
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div className="card" style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 22 }}>{paper?.title || 'Daily Test'}</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {paper?.status === 'completed' ? 'Submitted' : `Status: ${paper?.status || 'unknown'}`}
            {row?.submitted_at ? ` on ${new Date(row.submitted_at).toLocaleString()}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 16 }}>
            <div><div style={{ fontSize: 24, fontWeight: 800 }}>{paper?.total_questions ?? '—'}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Questions</div></div>
            <div><div style={{ fontSize: 24, fontWeight: 800 }}>{row?.correct ?? '—'}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Correct</div></div>
            <div><div style={{ fontSize: 24, fontWeight: 800, color: '#3498DB' }}>{row?.accuracy_pct != null ? `${Math.round(row.accuracy_pct)}%` : '—'}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Accuracy</div></div>
            <div><div style={{ fontSize: 24, fontWeight: 800 }}>{answered}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Answered</div></div>
          </div>
        </div>
        {paper?.status === 'in_progress' ? (
          <div className="card" style={{ marginBottom: 20, background: '#FEF3C7', borderColor: '#F59E0B' }}>
            <div style={{ fontSize: 14, color: '#92400E', marginBottom: 12 }}>
              This paper has not been submitted yet, so there is no result to show.
            </div>
            <button className="btn btn-primary" onClick={() => navigate(`/student/daily-paper/${testId}`)}>
              Go back and finish it
            </button>
          </div>
        ) : (
          <>
            <div className="card" style={{ marginBottom: 20, background: '#FEF3C7', borderColor: '#F59E0B' }}>
              <div style={{ fontSize: 14, color: '#92400E' }}>
                You opened this from a link or refreshed the page, so the "how the paper was built"
                and concept-by-concept breakdowns are not available here. The full question review
                below is complete, and you can still tag your mistakes.
              </div>
            </div>

            {needTagging.length > 0 && (
              <div className="card" style={{ marginBottom: 16, borderTop: '3px solid var(--primary)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{taggedCount} of {needTagging.length} tagged</div>
                  <span className={`badge ${taggedCount === needTagging.length ? 'badge-green' : 'badge-yellow'}`}>
                    {taggedCount === needTagging.length ? 'All done' : `${needTagging.length - taggedCount} left`}
                  </span>
                </div>
                <div style={{ marginTop: 10, height: 6, background: 'var(--border)', borderRadius: 3 }}>
                  <div style={{ height: '100%', borderRadius: 3, background: 'var(--primary)', width: `${needTagging.length ? (taggedCount / needTagging.length) * 100 : 0}%`, transition: 'width 0.2s' }} />
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 12 }}>
                  For each question you got wrong or skipped, say <em>why</em> it went wrong. A concept
                  you never learned needs teaching; a calculation slip needs practice, not teaching.
                  Tagging honestly is what makes tomorrow's paper aim at the right thing.
                </p>
              </div>
            )}

            {review.map((q, i) => (
              <ReviewCard key={q.question_id} q={q} index={i} testId={testId}
                tag={tags[q.question_id] || null} onTagged={onTagged} />
            ))}
          </>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => navigate('/student/daily-paper')}>Back to Daily Test</button>
          <button className="btn btn-ghost" onClick={() => navigate('/student/daily-paper/history')}>Past papers</button>
        </div>
      </div>
    );
  }

  // ---------- FULL RESULT ----------
  const d = passed;
  const skipped = (d.total || 0) - (d.attempted || 0);
  const scorePct = d.max_score > 0 ? Math.round((d.score / d.max_score) * 100) : 0;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header stats */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 24 }}>Score: {d.score} / {d.max_score}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
              {d.correct} correct · {d.incorrect} incorrect · {skipped} skipped
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: '#3498DB' }}>{(d.accuracy_pct || 0).toFixed(1)}%</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Accuracy</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: '#9B59B6' }}>{(d.attempt_pct || 0).toFixed(1)}%</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Attempt</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: scorePct >= 60 ? '#10B981' : scorePct >= 40 ? '#F59E0B' : '#EF4444' }}>{scorePct}%</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Score</div></div>
          </div>
        </div>
      </div>

      {/* How the paper was built */}
      {(d.by_bucket || []).length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, marginBottom: 4 }}>How this paper was built</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
            Every paper mixes four kinds of questions, so you are never only revising and never only learning.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {BUCKETS.map(b => {
              const row = (d.by_bucket || []).find(x => x.bucket === b.key);
              if (!row || !row.total) return null;
              const pct = row.total ? Math.round((row.correct / row.total) * 100) : 0;
              return (
                <div key={b.key} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{b.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{b.hint}</div>
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 22, fontWeight: 800 }}>{row.correct}<span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-muted)' }}> / {row.total}</span></span>
                    <span className={`badge ${pct >= 80 ? 'badge-green' : pct < 50 ? 'badge-red' : 'badge-yellow'}`}>{pct}% right</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-concept table, worst first */}
      {modules.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, marginBottom: 4 }}>Concept by concept</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
            Weakest at the top. These are the concepts to go back to first.
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>Concept</th>
                <th style={{ textAlign: 'center' }}>Right</th>
                <th style={{ textAlign: 'center' }}>Wrong</th>
                <th style={{ textAlign: 'center' }}>Skipped</th>
                <th style={{ textAlign: 'right' }}>Out of</th>
              </tr>
            </thead>
            <tbody>
              {modules.map(m => (
                <tr key={m.module_code}>
                  <td>
                    {m.module_name}
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{m.module_code}</div>
                  </td>
                  <td style={{ textAlign: 'center', color: 'var(--success)', fontWeight: 600 }}>{m.correct}</td>
                  <td style={{ textAlign: 'center', color: m.wrong ? 'var(--danger)' : 'var(--text-muted)', fontWeight: m.wrong ? 600 : 400 }}>{m.wrong}</td>
                  <td style={{ textAlign: 'center', color: m.skipped ? 'var(--warning)' : 'var(--text-muted)' }}>{m.skipped}</td>
                  <td style={{ textAlign: 'right' }}>{m.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tagging progress — the reason the student should scroll through the review */}
      {needTagging.length > 0 && (
        <div className="card" style={{ marginBottom: 16, borderTop: '3px solid var(--primary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {taggedCount} of {needTagging.length} tagged
            </div>
            <span className={`badge ${taggedCount === needTagging.length ? 'badge-green' : 'badge-yellow'}`}>
              {taggedCount === needTagging.length ? 'All done' : `${needTagging.length - taggedCount} left`}
            </span>
          </div>
          <div style={{ marginTop: 10, height: 6, background: 'var(--border)', borderRadius: 3 }}>
            <div style={{ height: '100%', borderRadius: 3, background: 'var(--primary)', width: `${needTagging.length ? (taggedCount / needTagging.length) * 100 : 0}%`, transition: 'width 0.2s' }} />
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 12 }}>
            For each question you got wrong or skipped, say <em>why</em> it went wrong. A concept
            you never learned needs teaching; a calculation slip needs practice, not teaching.
            Tagging honestly is what makes tomorrow's paper aim at the right thing.
          </p>
        </div>
      )}

      {review.map((q, i) => (
        <ReviewCard key={q.question_id} q={q} index={i} testId={testId}
          tag={tags[q.question_id] || null} onTagged={onTagged} />
      ))}

      <div style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={() => navigate('/student/daily-paper')}>Back to Daily Test</button>
        <button className="btn btn-ghost" onClick={() => navigate('/student/daily-paper/history')}>Past papers</button>
        <button className="btn btn-ghost" onClick={() => navigate('/student')}>Dashboard</button>
      </div>
    </div>
  );
}
